/**
 * Globale Seitennavigation — Gruppierung der bestehenden App-Einstiege.
 *
 * | Gruppe | Einstieg | Route |
 * | Menschen | Kunden | /trainingsdesk/kunden |
 * | Menschen | Lieferanten | /trainingsdesk/lieferanten |
 * | Menschen | Trainer | /trainingsdesk/trainer |
 * | Training | Trainingstermine | /trainingsdesk/planung |
 * | Training | Serientermine | /hub/serientermine |
 * | Training | Trainer Studio | /hub/trainer |
 * | Training | Trainerstunden | /trainer/sessions |
 * | Training | Abrechnung / Finanzen / Auswertung | … |
 * | Einstellungen | Eine Seite, SideNav springt zu Bereichen (?cat=) | /hub/einstellungen?cat=… |
 * | Insights | Übersicht / Trainings / Turniere / Clubmeisterschaft | /hub/insights… |
 */

import { buildKalenderHubHref } from "@tennis-matches/core";
import { normalizeSettingsHubCategoryId } from "@tennis-matches/services/platform";

export const PLATFORM_SIDE_NAV_COLLAPSED_KEY = "tm.platform.sideNav.collapsed";
export const PLATFORM_SIDE_NAV_EXPANDED_WIDTH_PX = 272;
export const PLATFORM_SIDE_NAV_COLLAPSED_WIDTH_PX = 68;
export const PLATFORM_SIDE_NAV_MOBILE_MQ = "(max-width: 767px)";

/** Kalender-Link mit gespeicherter Self-Person anreichern. */
export function resolvePlatformSideNavHref(href) {
  const raw = String(href || "").trim();
  if (!raw) return raw;
  const path = raw.split("?")[0];
  if (path !== "/hub/kalender" && path !== "/kalender") return raw;
  if (/[?&]persons=/.test(raw)) return raw;
  return buildKalenderHubHref({ view: "week" });
}

/**
 * @typedef {{
 *   kind?: "item" | "section",
 *   id: string,
 *   labelKey: string,
 *   href?: string,
 *   appKey?: string | null,
 *   matchPrefixes?: string[],
 *   matchExact?: string[],
 *   matchSearchCat?: string,
 *   requireEmptySearchCat?: boolean,
 * }} PlatformSideNavNode
 *
 * @typedef {{
 *   id: string,
 *   labelKey: string,
 *   href?: string,
 *   appKey?: string | null,
 *   matchPrefixes?: string[],
 *   matchExact?: string[],
 *   children?: PlatformSideNavNode[],
 * }} PlatformSideNavGroup
 */

/** @type {PlatformSideNavGroup[]} */
export const PLATFORM_SIDE_NAV_GROUPS = [
  {
    id: "home",
    labelKey: "nav.home",
    href: "/hub",
    appKey: null,
    matchPrefixes: [],
    matchExact: ["/hub", "/"],
  },
  {
    id: "calendar",
    labelKey: "hub.kalender.title",
    // persons= kommt zur Laufzeit via resolvePlatformSideNavHref (Self-Person).
    href: "/hub/kalender",
    appKey: "kalender",
    matchPrefixes: ["/hub/kalender", "/kalender"],
  },
  {
    id: "menschen",
    labelKey: "nav.side.group.menschen",
    // Gruppenklick: Alle Kunden (ohne Rollenfilter).
    href: "/trainingsdesk/kunden",
    children: [
      {
        kind: "item",
        id: "trainingsdesk-kunden",
        labelKey: "nav.side.menschen.customers",
        href: "/trainingsdesk/kunden",
        appKey: "trainerstunden",
        matchPrefixes: [
          "/trainingsdesk/kunden",
          "/hub/trainingsdesk/kunden",
          "/trainingsdesk/kontakte",
          "/hub/trainingsdesk/kontakte",
          "/verwaltung/kontakte",
        ],
      },
      {
        kind: "item",
        id: "trainingsdesk-lieferanten",
        labelKey: "nav.side.menschen.vendors",
        href: "/trainingsdesk/lieferanten?role=vendor",
        appKey: "trainerstunden",
        matchPrefixes: [
          "/trainingsdesk/lieferanten",
          "/hub/trainingsdesk/lieferanten",
        ],
      },
      {
        kind: "item",
        id: "trainingsdesk-trainer",
        labelKey: "nav.side.training.trainers",
        href: "/trainingsdesk/trainer",
        appKey: "trainerstunden",
        matchPrefixes: [
          "/trainingsdesk/trainer",
          "/hub/trainingsdesk/trainer",
          "/trainingsdesk/stammdaten/trainer",
          "/hub/trainingsdesk/stammdaten/trainer",
        ],
      },
    ],
  },
  {
    id: "training",
    labelKey: "nav.side.group.training",
    children: [
      {
        kind: "item",
        id: "trainingsdesk-planung",
        labelKey: "trainerstunden.tabs.planning",
        href: "/trainingsdesk/planung",
        appKey: "trainerstunden",
        matchPrefixes: ["/trainingsdesk/planung", "/hub/trainingsdesk/planung"],
        matchExact: [
          "/hub/trainingsdesk",
          "/trainingsdesk",
          "/trainerstunden",
          "/hub/trainerstunden",
        ],
      },
      {
        kind: "item",
        id: "serientermine",
        labelKey: "hub.winterdoppel.title",
        href: "/hub/serientermine",
        appKey: "winterdoppel",
        matchPrefixes: [
          "/hub/serientermine",
          "/serientermine",
          "/hub/regeltermine",
          "/regeltermine",
          "/winterdoppel",
          "/hub/winterdoppel",
        ],
      },
      {
        kind: "item",
        id: "trainer-studio",
        labelKey: "hub.trainer.title",
        href: "/hub/trainer",
        appKey: "trainer",
        matchPrefixes: [
          "/hub/trainer",
          "/trainer",
          "/trainer/lesson-plans",
          "/hub/trainer/lesson-plans",
          "/trainer/drills",
          "/hub/trainer/drills",
          "/trainer/rahmenplan-templates",
          "/hub/trainer/rahmenplan-templates",
        ],
        matchExact: ["/hub/trainer", "/trainer"],
      },
      {
        kind: "item",
        id: "trainer-sessions",
        labelKey: "nav.side.training.studioSessions",
        href: "/trainer/sessions",
        appKey: "trainer",
        navTier: "explore",
        matchPrefixes: ["/trainer/sessions", "/hub/trainer/sessions"],
      },
      {
        kind: "item",
        id: "trainingsdesk-abrechnung",
        labelKey: "trainerstunden.tabs.abrechnung",
        href: "/trainingsdesk/abrechnung",
        appKey: "trainerstunden",
        matchPrefixes: [
          "/trainingsdesk/abrechnung",
          "/hub/trainingsdesk/abrechnung",
          "/trainingsdesk/rechnung",
          "/hub/trainingsdesk/rechnung",
          "/trainingsdesk/rechnungsentwuerfe",
        ],
      },
      {
        kind: "item",
        id: "trainingsdesk-finanzen",
        labelKey: "trainerstunden.tabs.finanzen",
        href: "/trainingsdesk/zahlungen",
        appKey: "trainerstunden",
        matchPrefixes: [
          "/trainingsdesk/zahlungen",
          "/trainingsdesk/ausgaben",
          "/trainingsdesk/bilanzierung",
          "/hub/trainingsdesk/zahlungen",
          "/hub/trainingsdesk/ausgaben",
          "/hub/trainingsdesk/bilanzierung",
        ],
      },
    ],
  },
  {
    id: "tournaments",
    labelKey: "nav.side.group.tournaments",
    children: [
      {
        kind: "item",
        id: "matchmixer",
        labelKey: "hub.schleifchen.title",
        href: "/hub/matchmixer",
        appKey: "matchmixer",
        matchPrefixes: [
          "/hub/matchmixer",
          "/matchmixer",
          "/schleifchen",
          "/hub/schleifchen",
        ],
      },
      {
        kind: "item",
        id: "clubchamp",
        labelKey: "hub.clubmeisterschaft.title",
        href: "/hub/clubmeisterschaft",
        appKey: "clubmeisterschaft",
        matchPrefixes: [
          "/hub/clubmeisterschaft",
          "/clubmeisterschaft",
          "/clubchamp",
        ],
      },
      {
        kind: "item",
        id: "teamdesk",
        labelKey: "hub.medenspiele.title",
        href: "/hub/medenspiele",
        appKey: "medenspiele",
        matchPrefixes: [
          "/hub/medenspiele",
          "/medenspiele",
          "/teamdesk",
          "/hub/teamdesk",
        ],
      },
      {
        kind: "item",
        id: "teamdesk-squad",
        labelKey: "teamdesk.nav.squad",
        href: "/medenspiele/kader",
        appKey: "medenspiele",
        matchPrefixes: ["/medenspiele/kader", "/hub/medenspiele/kader"],
      },
      {
        kind: "item",
        id: "teamdesk-sessions",
        labelKey: "teamdesk.nav.sessions",
        href: "/medenspiele/termine",
        appKey: "medenspiele",
        matchPrefixes: ["/medenspiele/termine", "/hub/medenspiele/termine"],
      },
    ],
  },
  {
    id: "club",
    labelKey: "nav.side.group.club",
    children: [
      {
        kind: "item",
        id: "clubhub",
        labelKey: "hub.clubhub.title",
        href: "/hub/clubhub",
        appKey: "clubhub",
        matchPrefixes: ["/hub/clubhub", "/clubhub"],
        matchExact: ["/verwaltung"],
      },
      {
        kind: "item",
        id: "belegung",
        labelKey: "hub.belegung.title",
        href: "/hub/belegung",
        appKey: "belegung",
        matchPrefixes: ["/hub/belegung", "/belegung"],
      },
      {
        kind: "item",
        id: "belegung-bookings",
        labelKey: "belegung.nav.myBookings",
        href: "/hub/belegung/bookings",
        appKey: "belegung",
        matchPrefixes: [
          "/hub/belegung/bookings",
          "/belegung/bookings",
          "/meine-buchungen",
        ],
      },
      {
        kind: "item",
        id: "matchflow",
        labelKey: "hub.matchflow.title",
        href: "/hub/matchflow",
        appKey: "matchflow",
        navTier: "explore",
        matchPrefixes: ["/hub/matchflow", "/matchflow"],
      },
    ],
  },
  {
    id: "tenniswelt",
    labelKey: "hub.tenniswelt.title",
    children: [
      {
        kind: "item",
        id: "tenniswelt-home",
        labelKey: "hub.tenniswelt.title",
        href: "/hub/tenniswelt",
        appKey: "tennis-live",
        navTier: "explore",
        matchPrefixes: ["/hub/tenniswelt", "/tenniswelt"],
      },
      {
        kind: "item",
        id: "minigames",
        labelKey: "hub.minigames.title",
        href: "/hub/minigames",
        appKey: "minigames",
        navTier: "explore",
        matchPrefixes: [
          "/hub/minigames",
          "/minigames",
          "/minigames-reaction",
          "/minigames-memory",
          "/minigames-bubble-tap",
          "/bubble-tap",
        ],
      },
    ],
  },
  {
    id: "insights",
    labelKey: "nav.side.group.insights",
    children: [
      {
        kind: "item",
        id: "insights-overview",
        labelKey: "nav.side.insights.overview",
        href: "/hub/insights",
        appKey: "insights",
        matchExact: [
          "/hub/insights",
          "/insights",
          "/statistik",
          "/hub/statistik",
        ],
      },
      {
        kind: "item",
        id: "insights-trainings",
        labelKey: "nav.side.insights.trainings",
        href: "/hub/insights/trainingsdesk",
        appKey: "insights",
        matchPrefixes: [
          "/hub/insights/trainingsdesk",
          "/insights/trainingsdesk",
          "/hub/insights/trainerstunden",
          "/insights/trainerstunden",
          "/trainingsdesk/auswertung",
          "/hub/trainingsdesk/auswertung",
        ],
      },
      {
        kind: "item",
        id: "insights-tournaments",
        labelKey: "nav.side.insights.tournaments",
        href: "/hub/insights/matchmixer",
        appKey: "insights",
        matchPrefixes: [
          "/hub/insights/matchmixer",
          "/insights/matchmixer",
          "/hub/insights/schleifchen",
          "/insights/schleifchen",
        ],
      },
      {
        kind: "item",
        id: "insights-clubmeisterschaft",
        labelKey: "nav.side.insights.clubmeisterschaft",
        href: "/hub/insights/clubmeisterschaft",
        appKey: "insights",
        matchPrefixes: [
          "/hub/insights/clubmeisterschaft",
          "/insights/clubmeisterschaft",
          "/hub/insights/clubchamp",
          "/insights/clubchamp",
        ],
      },
    ],
  },
  {
    id: "einstellungen",
    labelKey: "nav.side.group.settings",
    // Gruppenklick öffnet die Einstellungen-Seite (Unterpunkte springen zu ?cat=).
    href: "/hub/einstellungen",
    matchExact: [
      "/hub/einstellungen",
      "/einstellungen",
      "/settings",
      "/hub/settings",
    ],
    children: [
      {
        kind: "item",
        id: "settings-club",
        labelKey: "settings.hub.category.club.title",
        href: "/hub/einstellungen?cat=club",
        appKey: null,
        matchSearchCat: "club",
        matchExact: [
          "/hub/einstellungen",
          "/einstellungen",
          "/settings",
          "/hub/settings",
        ],
        matchPrefixes: ["/access-requests-admin"],
      },
      {
        kind: "item",
        id: "settings-planning",
        labelKey: "settings.hub.category.planning.title",
        href: "/hub/einstellungen?cat=planning",
        appKey: null,
        matchSearchCat: "planning",
        matchExact: [
          "/hub/einstellungen",
          "/einstellungen",
          "/settings",
          "/hub/settings",
        ],
        matchPrefixes: [
          "/trainingsdesk/stammdaten/trainingsanlage",
          "/hub/trainingsdesk/stammdaten/trainingsanlage",
          "/trainingsdesk/stammdaten/auswahllisten",
          "/hub/trainingsdesk/stammdaten/auswahllisten",
          "/trainingsdesk/stammdaten/einstellungen",
          "/hub/trainingsdesk/stammdaten/einstellungen",
        ],
      },
      {
        kind: "item",
        id: "settings-venues",
        labelKey: "settings.hub.category.venues.title",
        href: "/hub/einstellungen?cat=venues",
        appKey: null,
        matchSearchCat: "venues",
        matchExact: [
          "/hub/einstellungen",
          "/einstellungen",
          "/settings",
          "/hub/settings",
        ],
        matchPrefixes: [
          "/verwaltung/orte",
          "/belegung/settings",
          "/belegung/admin",
          "/belegung/einstellungen",
        ],
      },
      {
        kind: "item",
        id: "settings-billing",
        labelKey: "settings.hub.category.billing.title",
        href: "/hub/einstellungen?cat=billing",
        appKey: null,
        /** Legacy-Abo: nur Pro/Gold (`features.billing`). */
        requiredFeature: "billing",
        matchSearchCat: "billing",
        matchExact: [
          "/hub/einstellungen",
          "/einstellungen",
          "/settings",
          "/hub/settings",
        ],
        matchPrefixes: [
          "/trainingsdesk/stammdaten/rechnungslayout",
          "/hub/trainingsdesk/stammdaten/rechnungslayout",
          "/trainingsdesk/stammdaten/datenexport",
          "/hub/trainingsdesk/stammdaten/datenexport",
        ],
      },
      {
        kind: "item",
        id: "settings-apps",
        labelKey: "settings.hub.category.apps.title",
        href: "/hub/einstellungen?cat=apps",
        appKey: null,
        matchSearchCat: "apps",
        matchExact: [
          "/hub/einstellungen",
          "/einstellungen",
          "/settings",
          "/hub/settings",
        ],
        matchPrefixes: [
          "/trainingsdesk/stammdaten/vereinsabo",
          "/hub/trainingsdesk/stammdaten/vereinsabo",
          "/trainingsdesk/vereinsabo",
        ],
      },
      {
        kind: "item",
        id: "settings-help",
        labelKey: "settings.hub.help",
        href: "/trainingsdesk/hilfe",
        appKey: null,
        matchPrefixes: ["/trainingsdesk/hilfe", "/hub/trainingsdesk/hilfe"],
      },
    ],
  },
];

function __normPath(pathname) {
  const p =
    String(pathname || "")
      .split("?")[0]
      .trim() || "/";
  if (p.length > 1 && p.endsWith("/")) return p.slice(0, -1);
  return p;
}

function __isSection(node) {
  return String(node?.kind || "") === "section";
}

function __parseSearchCat(search) {
  try {
    const raw = String(
      new URLSearchParams(String(search || "").replace(/^\?/, "")).get("cat") ||
        "",
    ).trim();
    return normalizeSettingsHubCategoryId(raw);
  } catch {
    return "";
  }
}

/** Sichtbare Children inkl. Section-Header (leere Abschnitte entfallen). */
export function filterPlatformSideNavChildren(children, itemVisible) {
  const raw = Array.isArray(children) ? children : [];
  /** @type {PlatformSideNavNode[]} */
  const out = [];
  let pendingSection = null;
  for (const node of raw) {
    if (__isSection(node)) {
      pendingSection = node;
      continue;
    }
    if (!itemVisible(node)) continue;
    if (pendingSection) {
      out.push(pendingSection);
      pendingSection = null;
    }
    out.push({ ...node, kind: node.kind || "item" });
  }
  return out;
}

/**
 * Normalisiert Suchtext für Side-Nav (Kleinbuchstaben, Umlaute → ae/oe/ue/ss).
 * @param {unknown} q
 * @returns {string}
 */
export function normalizeSideNavSearchQuery(q) {
  return String(q ?? "")
    .toLocaleLowerCase("de")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .replace(/ß/g, "ss")
    .trim();
}

/**
 * @param {string} label
 * @param {string} needleNorm
 */
function __sideNavLabelMatches(label, needleNorm) {
  if (!needleNorm) return false;
  const hay = normalizeSideNavSearchQuery(label);
  if (!hay) return false;
  if (hay.includes(needleNorm)) return true;
  // "abrechnung" trifft auch, wenn Query "Abrechnung" ohne Umlaute-Mapping war
  return hay.replace(/\s+/g, "").includes(needleNorm.replace(/\s+/g, ""));
}

/**
 * Flache Treffer für Apple-ähnliche Side-Nav-Suche (nur Leaf-Items).
 * `groups` sollte bereits zugangsbeschränkt sein (wie in PlatformSideNav).
 *
 * @param {PlatformSideNavGroup[]} groups
 * @param {{ t: (key: string) => string, query: string }} opts
 * @returns {Array<{ id: string, href: string, label: string, groupLabel: string, appKey?: string|null }>}
 */
export function collectPlatformSideNavSearchHits(groups, opts = {}) {
  const t = typeof opts?.t === "function" ? opts.t : (k) => k;
  const needle = normalizeSideNavSearchQuery(opts?.query);
  if (!needle) return [];

  /** @type {Array<{ id: string, href: string, label: string, groupLabel: string, appKey?: string|null }>} */
  const hits = [];
  const list = Array.isArray(groups) ? groups : [];

  for (const group of list) {
    const groupLabel = String(t(group.labelKey) || group.labelKey || "").trim();
    const children = Array.isArray(group.children) ? group.children : [];

    if (!children.length) {
      const href = String(group.href || "").trim();
      if (!href) continue;
      const label = groupLabel;
      if (!__sideNavLabelMatches(label, needle)) continue;
      hits.push({
        id: String(group.id),
        href,
        label,
        groupLabel: "",
        appKey: group.appKey ?? null,
      });
      continue;
    }

    for (const child of children) {
      if (__isSection(child)) continue;
      const href = String(child.href || "").trim();
      if (!href) continue;
      const label = String(t(child.labelKey) || child.labelKey || "").trim();
      const matchesLabel = __sideNavLabelMatches(label, needle);
      const matchesGroup = __sideNavLabelMatches(groupLabel, needle);
      if (!matchesLabel && !matchesGroup) continue;
      hits.push({
        id: String(child.id),
        href,
        label,
        groupLabel,
        appKey: child.appKey ?? null,
      });
    }
  }

  return hits;
}

/**
 * Längster Prefix-Match gewinnt (Unterseiten vor Gruppen-Roots).
 * Exact-Matches schlagen längere Prefixes derselben Länge + Bonus.
 * `matchSearchCat` schlägt Hub-Exact ohne Cat.
 * @param {string} pathname
 * @param {PlatformSideNavGroup[]} [groups]
 * @param {string} [search]
 */
export function resolveActiveSideNavIds(
  pathname,
  groups = PLATFORM_SIDE_NAV_GROUPS,
  search = "",
) {
  const path = __normPath(pathname);
  const cat = __parseSearchCat(search);
  let best = { groupId: "", itemId: "", score: -1 };

  const consider = (groupId, itemId, prefixes, exact = false, bonus = 0) => {
    for (const raw of prefixes || []) {
      const prefix = __normPath(raw);
      if (!prefix) continue;
      const hit = exact
        ? path === prefix
        : path === prefix || path.startsWith(`${prefix}/`);
      if (!hit) continue;
      const score = (exact ? prefix.length + 1000 : prefix.length) + bonus;
      if (score > best.score) {
        best = { groupId, itemId: itemId || "", score };
      }
    }
  };

  for (const group of groups) {
    if (Array.isArray(group.children) && group.children.length) {
      for (const child of group.children) {
        if (__isSection(child)) continue;
        const searchCat = String(child.matchSearchCat || "").trim();
        if (searchCat) {
          // Hub mit ?cat=: exakter Bereich gewinnt.
          if (cat === searchCat) {
            consider(group.id, child.id, child.matchExact || [], true, 2000);
          } else if (!cat) {
            // Hub-Übersicht ohne ?cat=: Gruppe bleibt aktiv → Unterpunkte aufklappen.
            consider(group.id, child.id, child.matchExact || [], true, 500);
          }
          // Detailseiten ohne ?cat= über Prefixes (z. B. /vereinsabo).
          consider(group.id, child.id, child.matchPrefixes || []);
          continue;
        }
        if (child.requireEmptySearchCat && cat) {
          consider(group.id, child.id, child.matchPrefixes || [], false);
          continue;
        }
        const exact = child.matchExact || [];
        if (exact.length) {
          consider(group.id, child.id, exact, true);
        }
        const prefixes = child.matchPrefixes
          ? child.matchPrefixes
          : child.href && !String(child.href).includes("?")
            ? [child.href]
            : [];
        consider(group.id, child.id, prefixes);
      }
    } else {
      consider(group.id, "", group.matchExact || [], true);
      consider(
        group.id,
        "",
        group.matchPrefixes ||
          (group.href && !group.matchExact ? [group.href] : []),
      );
    }
  }

  if (!best.groupId && (path === "/hub" || path === "/")) {
    return { groupId: "home", itemId: "" };
  }

  return { groupId: best.groupId, itemId: best.itemId };
}
